import { getAuditLogDetail } from './api';

const MAX_STACK_PREVIEW = 3;

const isAttendanceSent = (value) =>
  value === true || value === 1 || value === '1' || value === 'true';

const getLogTimestampMs = (log) => {
  const ts = log?.captured_at || log?.created_at;
  if (!ts) return Number.NaN;
  const parsed = Date.parse(ts);
  return Number.isNaN(parsed) ? Number.NaN : parsed;
};

const getLogRequestMetadata = (log) => {
  if (!log) return {};
  const raw = log.request_metadata;
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch (e) {
      return {};
    }
  }
  return {};
};

const getAttendanceRefFromLog = (log) => {
  const metadata = getLogRequestMetadata(log);
  const topLevelManualAttendance = log?.manual_attendance && typeof log.manual_attendance === 'object'
    ? log.manual_attendance
    : null;
  const manualAttendance = metadata.manual_attendance && typeof metadata.manual_attendance === 'object'
    ? metadata.manual_attendance
    : null;
  const attendanceMeta = metadata.attendance && typeof metadata.attendance === 'object'
    ? metadata.attendance
    : null;

  const attendanceId =
    (topLevelManualAttendance && topLevelManualAttendance.attendance_id) ||
    (manualAttendance && manualAttendance.attendance_id) ||
    (attendanceMeta && attendanceMeta.attendance_id) ||
    log?.attendance_id ||
    metadata.attendance_id ||
    null;

  return {
    attendanceId: attendanceId ? String(attendanceId) : null,
  };
};

const getStackLogKey = (log) => {
  if (!log) return null;
  if (log.audit_id) return `audit:${String(log.audit_id)}`;
  const timestamp = log.captured_at || log.created_at || '';
  const camera = (log.camera || '').toString().toLowerCase();
  return `fallback:${String(log.track_id || 'track')}:${camera}:${String(timestamp)}`;
};

/** Prefer attendance-marked frame so modal top frame matches the Attendance chip. */
export const pickAttendanceMarkedLogForDisplay = (representativeLog, bucketLogs = []) => {
  const entries = [representativeLog, ...(Array.isArray(bucketLogs) ? bucketLogs : [])].filter(Boolean);
  if (!entries.length) return representativeLog || null;

  const scored = entries.map((item) => {
    const ref = getAttendanceRefFromLog(item);
    const ts = getLogTimestampMs(item);
    return {
      item,
      ts: Number.isNaN(ts) ? Number.NEGATIVE_INFINITY : ts,
      hasAttendanceId: Boolean(ref.attendanceId),
      hasAttendanceSent: isAttendanceSent(item?.send_attendance),
    };
  });

  const attendanceCandidates = scored.filter((s) => s.hasAttendanceId || s.hasAttendanceSent);
  if (!attendanceCandidates.length) return representativeLog || entries[0];

  attendanceCandidates.sort((a, b) => {
    const byId = Number(b.hasAttendanceId) - Number(a.hasAttendanceId);
    if (byId) return byId;
    const bySent = Number(b.hasAttendanceSent) - Number(a.hasAttendanceSent);
    if (bySent) return bySent;
    return b.ts - a.ts;
  });

  return attendanceCandidates[0].item;
};

/** Put representative (usually attendance-marked) first, then remaining bucket frames. */
export const buildPrioritizedStackImages = (bucketLogs, representativeLog, imageCache = {}) => {
  const logs = Array.isArray(bucketLogs) ? bucketLogs : [];
  const orderedLogs = [];
  const seen = new Map();

  const addLog = (entry) => {
    const key = getStackLogKey(entry);
    if (!key) return;
    if (seen.has(key)) {
      const existingIndex = seen.get(key);
      orderedLogs[existingIndex] = {
        ...orderedLogs[existingIndex],
        ...entry,
      };
      return;
    }
    seen.set(key, orderedLogs.length);
    orderedLogs.push(entry);
  };

  addLog(representativeLog);
  logs.forEach(addLog);

  return orderedLogs.slice(0, MAX_STACK_PREVIEW).map((stackLog) => ({
    log: stackLog,
    image: stackLog.audit_id ? imageCache[stackLog.audit_id] : null,
  }));
};

/**
 * Enrich a Possible Match tile with full top_candidates (up to 3) from detail.
 * List payloads only need card preview; the modal loads remaining matcher slots on click.
 */
export async function hydratePossibleMatchLogFromDetail(log) {
  const auditId = log?.audit_id;
  if (!auditId) return log;

  const detail = await getAuditLogDetail(auditId, { include_bucket: false });
  if (!detail || !detail.audit_id) return log;

  const detailMeta =
    detail.request_metadata && typeof detail.request_metadata === 'object' && !Array.isArray(detail.request_metadata)
      ? detail.request_metadata
      : {};

  return {
    ...log,
    request_metadata: {
      ...(log.request_metadata || {}),
      ...detailMeta,
    },
  };
}

/**
 * Fetch full audit detail for the processing-timeline modal.
 * List payloads are slim; modal fields come from GET /audit/logs/{audit_id}?include_bucket=true
 * Re-applies attendance-first stack priority after detail loads (API returns time order).
 */
export async function hydrateTimingDialogFromDetail(log, stack = []) {
  const auditId = log?.audit_id;
  if (!auditId) {
    return { log, stack, detailLoading: false };
  }

  const detail = await getAuditLogDetail(auditId, { include_bucket: true });
  if (!detail || !detail.audit_id) {
    return { log, stack, detailLoading: false };
  }

  const bucketLogs = Array.isArray(detail.bucket_logs) && detail.bucket_logs.length > 0
    ? detail.bucket_logs
    : [detail];

  const existingById = new Map();
  (Array.isArray(stack) ? stack : []).forEach((frame) => {
    const id = frame?.log?.audit_id;
    if (id) existingById.set(String(id), frame);
  });

  const imageCache = {};
  existingById.forEach((frame, id) => {
    if (frame?.image) imageCache[id] = frame.image;
  });

  const mergedBucketLogs = bucketLogs.map((bucketLog) => {
    const existing = existingById.get(String(bucketLog?.audit_id || ''));
    return { ...(existing?.log || {}), ...bucketLog };
  });

  const representativeBase = {
    ...log,
    ...detail,
  };
  const attendanceMarkedLog = pickAttendanceMarkedLogForDisplay(representativeBase, mergedBucketLogs);
  const topLog =
    attendanceMarkedLog && attendanceMarkedLog !== representativeBase
      ? { ...representativeBase, ...attendanceMarkedLog }
      : representativeBase;

  const prioritizedStack = buildPrioritizedStackImages(mergedBucketLogs, topLog, imageCache);

  const mergedLog = {
    ...topLog,
    capturedTs: log?.capturedTs,
    requestSentTs: log?.requestSentTs,
    apiSubmitTs: log?.apiSubmitTs,
    apiReceivedTs: log?.apiReceivedTs,
  };

  if (prioritizedStack.length === 0) {
    return { log: mergedLog, stack, detailLoading: false };
  }

  return { log: mergedLog, stack: prioritizedStack, detailLoading: false };
}
